<template>
  <div
    ref="videoElement"
    class="video-element"
    :class="[
      { 'is-loading' : state === 'loading' },
      { 'is-errored' : state === 'error' },
      { 'video-element--background' : !inlinePlayer },
      { 'video-element--inline' : inlinePlayer },
    ]"
  >
    <video
      v-if="displayedVideo"
      ref="videoPlayer"
      :key="videoKey"
      class="video-element__player"
      :autoplay="shouldAutoplay"
      :controls="inlinePlayer"
      :height="height"
      :loop="isLoop"
      :muted="muted"
      :playsinline="inlinePlayer"
      :poster="displayedVideo.coverImage.src"
      :width="width"
      @ended="togglePlayBtn"
    >
      <source
        v-for="(source, index) in displayedVideo.sources"
        :key="`${index}-${source.mimeType}-${source.format}`"
        :src="source.url"
        :type="source.mimeType"
      >
    </video>

    <div
      v-if="!inlinePlayer && state !== 'error'"
      class="video-element__controls"
      js-video-player="controls"
    >
      <button
        ref="playButton"
        class="video-element__button video-element__button-pause"
        :aria-label="playButtonText"
        @click.prevent="togglePlayState(), toggleAutoplayState()"
      >
        <component :is="icon.pause" />
      </button>

      <button
        ref="volumeButton"
        class="video-element__button video-element__button-volume"
        :aria-label="volumeButtonText"
        @click.prevent="toggleMutedState"
      >
        <component :is="icon.unmute" />
      </button>
    </div>
    <div
      v-if="state === 'error'"
      class="video-element__error"
    >
      <p>{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>

/**
 * Component: Video Element
 * ------------------------------------------------------------------------------
 * - Core component for a Video Element component
 *
 * @param {Boolean} [autoPlay] - If true, video will autoplay as it enters the viewport.
 * @param {Boolean} [loop] - Loop a background video. (optional)
 * @param {Boolean} [inlinePlayer] - Display the video as an inline
 * element instead of background
 * @param {Object} video - A formatted image object.
 * @param {Object} [videoDesktop] - A formatted video object - specific to desktop. (>= 1024px)
 * @param {String} [videoExternalUrl] - An external video URL
 * @param {String} [videoExternalDesktopUrl] - An external video URL (desktop)
 *
 */
import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import { CloudinaryURL } from '~/helpers/cloudinary-url'
import { setIntersectionObserver } from '~/helpers/dom'

import IconPause from '~icons/media/pause.svg'
import IconPlay from '~icons/media/play.svg'
import IconMute from '~icons/media/mute.svg'
import IconUnmute from '~icons/media/unmute.svg'

export default {
  name: 'VideoElement',

  components: {
    IconMute,
    IconPause,
    IconPlay,
    IconUnmute,
  },

  props: {
    autoplay: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      required: false,
      default: null,
    },
    inlinePlayer: {
      type: Boolean,
      default: false,
    },
    video: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    videoDesktop: {
      type: [Boolean, Object],
      default: false,
    },
    videoExternalUrl: {
      type: String,
      default: '',
    },
    videoExternalDesktopUrl: {
      type: String,
      default: '',
    },
  },

  emits: ['set-aspect-ratio'],

  data() {
    return {
      displayedVideo: false,
      height: 0,
      icon: {
        pause: 'icon-play',
        unmute: 'icon-unmute',
      },
      isAutoplaying: true,
      muted: false,
      paused: true,
      state: 'loading',
      videoKey: `video-${new Date().toISOString()}`,
      width: 0,
      errorMessage: '',
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate the 'Play' button text.
     */
    playButtonText() {
      const string = this.paused ? 'play' : 'pause'

      return mint.strings.accessibility.video[string]
    },

    /**
     * Calculate if the video should autoplay by default. Video must also be
     * muted for autoplay to work in all browsers
     * @returns {Boolean}
     */
    shouldAutoplay() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return false
      }

      return this.autoplay
    },

    /**
     * Calculate the 'Volume' button text.
     */
    volumeButtonText() {
      const string = this.muted ? 'unmute' : 'mute'

      return mint.strings.accessibility.video[string]
    },

    /**
     * Calculate if the video should have loop based on the passed parameter.
     * By default using inlinePlayer prop to compute this
     * @returns {Boolean}
     */
    isLoop() {
      if (this.loop !== null) {
        return this.loop
      }

      return !this.inlinePlayer
    },
  },

  watch: {

    /**
     * Listen for if the device has been resized to mobile or desktop.
     */
    isMobile() {
      this.state = 'loading'
      this.setVideoProperties()
    },
  },

  mounted () {
    this.setVideoProperties()

    if (this.$refs.videoElement && this.video && this.autoplay) {
      setIntersectionObserver({
        callback: this.handleVideoIntersection,
        element: this.$refs.videoElement,
        threshold: 0.33,
      })
    }
  },

  methods: {

    /**
       * Get video source for desktop/mobile.
       * If we have a Cloudinary (external) URL, create a CloudinaryURL and add
       * a blank pipeline at the end, to which we add quality and format options.
       * The CloudinaryURL toVideoObject method outputs a shopify-like video
       * object.
       */
    getVideoObject() {
      const externalVideoUrl = !this.isMobile && this.videoExternalDesktopUrl
        ? this.videoExternalDesktopUrl
        : this.videoExternalUrl

      if (externalVideoUrl) {
        try {
          return new CloudinaryURL(externalVideoUrl)
            .addPipeline()
            .setStepValueByTransformName('quality', 'auto')
            .setStepValueByTransformName('format', 'auto:video', -1)
            .toVideoObject()
        } catch (err) {
          this.state = 'error'
          this.errorMessage = err
        }

      } else {
        return !this.isMobile && this.videoDesktop
          ? this.videoDesktop
          : this.video
      }
    },

    /**
     * Set the correct player properties depending on user preferences, device
     * and video format.
     */
    setVideoProperties() {
      this.state = 'loading'

      this.displayedVideo = this.getVideoObject()

      if (!this.displayedVideo) {
        return
      }

      /**
       * Set dimensions.
       */
      this.height = this.displayedVideo.sources
        ? this.displayedVideo.sources[0].height
        : '100%'

      this.width = this.displayedVideo.sources
        ? this.displayedVideo.sources[0].width
        : '100%'

      /**
       * Set muted state when background video.
       */
      if (
        !this.inlinePlayer ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.muted = true
        this.paused = true
      }

      /**
       * Set muted state when autoplay enabled. (Chromium browsers do not allow
       * autoplay in most cases, but muted autoplay is always allowed.)
       */
      if (this.shouldAutoplay) {
        this.muted = true
      }

      /**
       * Set a unique key for the video element so that it can rehydrate.
       */
      this.videoKey = `video-${new Date().valueOf()}`

      this.state = 'ready'
    },

    /**
     * Handle events for when the video is scrolled into view.
     * - If the video is not in the viewport, pause the video.
     * - If the user at any point has clicked 'Pause', do not do anything.
     * @param {Array} entries - Array of Intersection Observer entries.
     */
    handleVideoIntersection(entries) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return
      }

      entries.forEach((entry) => {
        if (!this.$refs.videoPlayer || !this.isAutoplaying) {
          return
        }

        if (entry.isIntersecting && this.paused) {
          this.togglePlayState()
          return
        }

        /**
         * Set the 'Pause' state to true so `togglePlayState` will pause the video.
         */
        this.paused = false
        this.togglePlayState()
      })
    },

    /**
     * Toggles the state of autoplaying.
     * - By default, for background players this will be true
     * - If true, on IntersectionObserver, auto-pause on scroll
     * - If false, the user has manually intercepted controls so don't interfere.
     */
    toggleAutoplayState() {
      this.isAutoplaying = false
    },

    /**
     * Toggle the videos playing state.
     * @param {Boolean} [shouldPlay] - define if the video should play on tap.
     */
    togglePlayState() {
      if (this.paused) {
        this.$refs.videoPlayer.play()
        this.icon.pause = 'icon-pause'
        this.paused = false
        return
      }

      this.$refs.videoPlayer.pause()
      this.icon.pause = 'icon-play'
      this.paused = true
    },

    /**
     * Toggle the volume of the video.
     */
    toggleMutedState() {
      if (!this.$refs.videoPlayer) {
        return
      }

      this.muted = !this.muted
      this.$refs.videoPlayer.muted = this.muted

      this.icon.unmute = this.muted ? 'icon-unmute' : 'icon-mute'
    },

    /**
     * Changes the `icon-pause` icon to `icon-play`, when the video has reached
     * the end (when the loop is disabled).
     */
    togglePlayBtn() {
      if (this.isLoop) {
        return
      }

      this.paused = true
      this.icon.pause = 'icon-play'
    },
  },
}
</script>
